// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";

import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import { formatDate } from "@transformers/date";
import type { State } from "@store/reducers";
import type { PaginationType } from "@types";
import config from "@config";
import { Button, GSGrid, Icons, Pagination, View, Text } from "@components";
import type {
  EmployeeSmsSettings,
  EmployeeSmsRule,
} from "@modules/service/reducer/employee-sms-notifications/types";

type PropsFromState = $Exact<{
  pagination: PaginationType,
  totalElements: number,
}>;

type PropsFromDispatch = $Exact<{
  getRules: typeof actionCreators.GetEmployeeSmsRules,
}>;

type Props = $Exact<{
  settings: ?EmployeeSmsSettings,
  list: Array<EmployeeSmsRule>,
  sort: Object,
  sortChange: () => void,
  onAdd: () => {},
  onEdit: (item: EmployeeSmsRule) => () => {},
  onDelete: (item: EmployeeSmsRule) => () => {},
  clientId: number,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export function HealthPromotions(props: Props) {
  const columns = [
    {
      name: props.t("ID"),
      key: "id",
      sortable: true,
    },
    {
      name: props.t("EMPLOYEE_SMS_WELLBEING_ABSENCE_REASON"),
      key: "absenceReason",
      align: "left",
      renderCell: ({ row }: { row: EmployeeSmsRule }) => row.absenceReason.label,
      minWidth: 150,
    },
    {
      name: props.t("EMPLOYEE_SMS_WELLBEING_TEXT_MESSAGE"),
      key: "textMessage",
      align: "left",
      sortable: true,
      width: 740,
      renderCell: ({ row }: { row: EmployeeSmsRule }) => GSGrid.render.ellipsis(row.textMessage),
    },
    {
      name: props.t("EMPLOYEE_SMS_WELLBEING_DATE_ADDED"),
      key: "createdAt",
      sortable: true,
      renderCell: ({ row }: { row: EmployeeSmsRule }) => formatDate(row.createdAt),
    },
    {
      key: "status",
      name: props.t("EMPLOYEE_SMS_WELLBEING_STATUS"),
      align: "center",
      width: 100,
      frozen: "right",
      renderCell: ({ row }: { row: EmployeeSmsRule }) =>
        GSGrid.render.statusPill(props.t(row.isActive ? "ACTIVE" : "INACTIVE")),
    },
    {
      name: props.t("EDIT"),
      key: "edit",
      align: "center",
      width: 100,
      frozen: "right",
      onCellClick: (row: EmployeeSmsRule) => props.onEdit(row),
      renderCell: ({ row }: { row: EmployeeSmsRule }) => GSGrid.render.materialIcon("edit"),
    },
    {
      name: props.t("DELETE"),
      key: "delete",
      align: "center",
      width: 100,
      frozen: "right",
      onCellClick: (row: EmployeeSmsRule) => props.onDelete(row),
      renderCell: ({ row }: { row: EmployeeSmsRule }) => GSGrid.render.materialIcon("delete"),
    },
  ];

  function totalPages() {
    const totalElements = props.totalElements || 0;
    const totalPages = totalElements ? Math.ceil(totalElements / config.resultsPerPage) : 0;
    return totalPages;
  }

  function handlePaginationChange(page: number) {
    props.getRules({
      clientId: props.clientId,
      page,
    });
  }

  return (
    <View>
      <View margin="0 0 1rem 0" direction="row" justifyContent="end">
        <Button materialIcon="add_box" onClick={props.onAdd}>
          {props.t("ADD")}
        </Button>
      </View>
      {props.list && (
        <GSGrid
          onSortChange={props.sortChange}
          sort={props.sort}
          rows={props.list}
          columns={columns}
        />
      )}
      {totalPages() > 1 && (
        <Pagination
          className="my-3 d-flex flex-fill justify-content-center"
          currentPage={props.pagination.pageNumber}
          totalPages={totalPages()}
          onChange={handlePaginationChange}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State, props: Props): PropsFromState => ({
  pagination: serviceSelectors.getEmployeeSmsRulesPagination(state),
  totalElements: serviceSelectors.getEmployeeSmsRulesTotalElements(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getRules: actionCreators.GetEmployeeSmsRules,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(HealthPromotions);
